// Packages
import { Button, FieldGroup, Select, TextField } from "@jkast90/kast-common/ui";
import { useState, type FormEvent } from "react";

// Components
import { BaseDialog } from "../ui";

// Styling
import styles from "./ReminderDialog.module.css";
import dialogCss from "../../styles/Dialog.module.css";

export default function ReminderDialog({
  open,
  onClose,
  onSet,
  onCancelReminder,
  initialDate,
}: {
  open: boolean;
  onClose: () => void;
  /** Called with the reminder's delay in minutes (always > 0). */
  onSet: (minutes: number) => void;
  onCancelReminder: () => void;
  /** The current reminder's due time, if one is set. */
  initialDate?: string | number | null;
}) {
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("minutes");

  // Start empty each time the dialog opens (adjusting state on a prop
  // change during render, rather than in an effect).
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setValue("");
      setUnit("minutes");
    }
  }

  const toMinutes = (val: string, unit: string): number | null => {
    const n = parseFloat(val);
    if (isNaN(n)) return null;
    const conversion: Record<string, number> = {
      seconds: n / 60,
      minutes: n,
      hours: n * 60,
      days: n * 1440,
    };
    return conversion[unit] ?? null;
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const minutes = toMinutes(value, unit);
    if (minutes && minutes > 0) onSet(minutes);
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="Set Reminder"
      className={styles.dialog}
    >
      <form onSubmit={handleSubmit} className={`kui-form ${dialogCss.form}`}>
        <FieldGroup label="Remind me in:">
          {(ids) => (
            <div
              className={styles.durationRow}
              role="group"
              aria-labelledby={ids.labelId}
            >
              <TextField
                variant="bare"
                aria-label="Amount"
                type="number"
                min="0"
                step="any"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
              />
              <Select
                variant="bare"
                aria-label="Unit"
                value={unit}
                onChange={setUnit}
                options={["minutes", "hours", "days", "seconds"].map((u) => ({
                  value: u,
                  label: u[0]!.toUpperCase() + u.slice(1),
                }))}
              />
            </div>
          )}
        </FieldGroup>

        <div className="kui-form-actions">
          {initialDate && (
            <Button onClick={onCancelReminder}>Cancel Reminder</Button>
          )}
          <Button onClick={onClose}>Close</Button>
          <Button type="submit" variant="primary">
            Save
          </Button>
        </div>
      </form>
    </BaseDialog>
  );
}
